import React, { useEffect, useState, useRef } from 'react';
import {
  getAuth,
  signIn,
  checkServiceAccess,
  logOut,
} from '@mirats/mirats-auth';

const ProtectedRoute = ({ children }) => {
  const [auth, setAuth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState(null);
  const [checkingAccess, setCheckingAccess] = useState(false);
  const hasCalledSignInRef = useRef(false);
  const hasCheckedAccessRef = useRef(false);

  const isDevelopment = () => {
    return (
      process.env.NODE_ENV === 'development' ||
      process.env.REACT_APP_ENV === 'development' ||
      process.env.REACT_APP_ENV === 'dev'
    );
  };

  const getServiceUrl = () => {
    if (typeof window !== 'undefined') {
      const envServiceUrl = process.env.REACT_APP_SERVICE_URL;
      if (envServiceUrl) {
        return envServiceUrl;
      }
      return window.location.origin;
    }
    return '';
  };

  const ACCESS_CACHE_KEY = 'diy_service_access_cache';
  const ACCESS_CACHE_TTL_MS = 6.5 * 60 * 60 * 1000; // 6.5 hours

  const getCachedAccess = (userId) => {
    try {
      const raw = localStorage.getItem(ACCESS_CACHE_KEY);
      if (!raw) return null;
      const cached = JSON.parse(raw);
      if (!cached || cached.userId !== userId) return null;
      if (cached.expiresAt && Date.now() > cached.expiresAt) return null;
      return cached.hasAccess;
    } catch {
      return null;
    }
  };

  const setCachedAccess = (userId, hasAccess) => {
    try {
      localStorage.setItem(
        ACCESS_CACHE_KEY,
        JSON.stringify({
          userId,
          hasAccess,
          expiresAt: Date.now() + ACCESS_CACHE_TTL_MS,
        })
      );
    } catch {
      /* ignore */
    }
  };

  useEffect(() => {
    const checkAuth = async () => {
      try {
        setLoading(true);
        const urlParams = new URLSearchParams(window.location.search);
        const hasTokenInUrl = urlParams.has('t');

        if (hasTokenInUrl) {
          await new Promise((resolve) => setTimeout(resolve, 500));
        }

        const authData = await getAuth();
        setAuth(authData);

        if (
          !authData?.currentUser &&
          !hasTokenInUrl &&
          !hasCalledSignInRef.current
        ) {
          hasCalledSignInRef.current = true;
          signIn();
          return;
        }

        if (authData?.currentUser && !hasCheckedAccessRef.current) {
          hasCheckedAccessRef.current = true;

          if (isDevelopment()) {
            setHasAccess(true);
            setCheckingAccess(false);
          } else {
            const userId = authData?.currentUser?._id || authData?.currentUser?.id || '';
            const cached = getCachedAccess(userId);
            if (cached !== null) {
              setHasAccess(cached);
              setCheckingAccess(false);
              return;
            }

            setCheckingAccess(true);
            const serviceUrl = getServiceUrl();

            if (serviceUrl) {
              try {
                const access = await checkServiceAccess(serviceUrl);
                setHasAccess(access);
                setCachedAccess(userId, access);
              } catch (error) {
                console.error(
                  '[ProtectedRoute] Error checking service access:',
                  error
                );
                setHasAccess(false);
                setCachedAccess(userId, false);
              } finally {
                setCheckingAccess(false);
              }
            } else {
              setHasAccess(true);
              setCheckingAccess(false);
            }
          }
        }
      } catch (error) {
        console.error('[ProtectedRoute] Auth error:', error);
        setAuth(null);
        setHasAccess(false);
        const urlParams = new URLSearchParams(window.location.search);
        if (!urlParams.has('t') && !hasCalledSignInRef.current) {
          hasCalledSignInRef.current = true;
          signIn();
        }
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, []);

  if (loading || checkingAccess) {
    return (
      <div className="enterprise-fullpage-loader">
        <div className="enterprise-fullpage-loader__content">
          <div className="enterprise-loader enterprise-loader--lg" role="progressbar" aria-label="Loading" />
          <p className="enterprise-fullpage-loader__text">
            {checkingAccess ? 'Verifying access...' : 'Loading...'}
          </p>
          <p className="enterprise-fullpage-loader__subtext">
            {checkingAccess ? 'Checking your permissions' : 'Please wait a moment'}
          </p>
        </div>
      </div>
    );
  }

  if (!auth?.currentUser) {
    return null;
  }

  if (hasAccess === false && !isDevelopment()) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          gap: '1rem',
        }}
      >
        <div style={{ textAlign: 'center' }}>
          <h1
            style={{
              fontSize: '1.5rem',
              fontWeight: 700,
              color: '#111',
              marginBottom: '1rem',
            }}
          >
            Access Denied
          </h1>
          <p style={{ color: '#4b5563' }}>
            You do not have permission to access this service.
          </p>
          <p style={{ fontSize: '0.875rem', color: '#6b7280' }}>
            Please contact your administrator if you believe this is an error.
          </p>
        </div>
        <button
          type="button"
          onClick={() => logOut()}
          style={{
            padding: '0.5rem 1rem',
            backgroundColor: '#111',
            color: '#fff',
            border: 'none',
            borderRadius: '0.25rem',
            cursor: 'pointer',
            fontSize: '1rem',
          }}
        >
          Logout and Re-Login
        </button>
      </div>
    );
  }

  if (hasAccess === null && auth?.currentUser) {
    return (
      <div className="enterprise-fullpage-loader">
        <div className="enterprise-fullpage-loader__content">
          <div className="enterprise-loader enterprise-loader--lg" role="progressbar" aria-label="Loading" />
          <p className="enterprise-fullpage-loader__text">Verifying access...</p>
          <p className="enterprise-fullpage-loader__subtext">Checking your permissions</p>
        </div>
      </div>
    );
  }

  return children;
};

export default ProtectedRoute;
